import { getAbsolutePublicAssetUrl } from "./thermalPrintCommon";

const THERMAL_PRINT_URL =
  process.env.REACT_APP_THERMAL_PRINT_URL || "http://localhost:3005/print";

const THERMAL_LOGO_PUBLIC_PATH =
  process.env.REACT_APP_THERMAL_LOGO_PUBLIC_PATH || "/img/asa_logo.png";

const COMPANY_NAME =
  process.env.REACT_APP_COMPANY_NAME || "ASA Transport";
const COMPANY_ADDRESS =
  process.env.REACT_APP_COMPANY_ADDRESS || "Mannargudi, Thiruvarur Dt.";

const normalize = (s) => (s || "").trim().toLowerCase();

function pickPrinterSettingValue(settings, key) {
  return settings.find(
    (s) =>
      normalize(s.setting_group) === "printer settings" &&
      normalize(s.setting_key) === normalize(key)
  )?.setting_value;
}

function isTruthySetting(val) {
  return normalize(val) === "true" || val === "1";
}

function guessImageMimeFromUrl(url) {
  try {
    const path = new URL(url).pathname.toLowerCase();
    if (path.endsWith(".png")) return "image/png";
    if (path.endsWith(".jpg") || path.endsWith(".jpeg")) return "image/jpeg";
    if (path.endsWith(".webp")) return "image/webp";
    if (path.endsWith(".gif")) return "image/gif";
  } catch {
    /* ignore */
  }
  return undefined;
}

function fmt2(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(2) : "0.00";
}

/** Column widths for 48-char line: Sl.No, Km, Rate, Ton, Amount, Wgt, Adv */
const TRIP_TABLE_COL_WIDTHS = [5, 3, 7, 7, 10, 7, 9];

function tripTableColWidths(lineWidth) {
  const baseSum = TRIP_TABLE_COL_WIDTHS.reduce((a, b) => a + b, 0);
  if (lineWidth === baseSum) return TRIP_TABLE_COL_WIDTHS;
  const extra = lineWidth - baseSum;
  return TRIP_TABLE_COL_WIDTHS.map((w, i) =>
    i === 4 ? w + extra : w
  );
}

function padBetween(left, right, lineWidth) {
  const l = String(left);
  const r = String(right);
  const gap = lineWidth - l.length - r.length;
  if (gap >= 1) return l + " ".repeat(gap) + r;
  if (l.length + r.length <= lineWidth) return l + r;
  return l.slice(0, Math.max(0, lineWidth - r.length)) + r;
}

function fitCell(text, cols) {
  const s = String(text ?? "");
  return s.length <= cols ? s : s.slice(0, cols);
}

export function isThermalPrinterFromSettings(settings) {
  return normalize(pickPrinterSettingValue(settings, "printer type")) === "thermal";
}

export function getReceiptPrinterDisplayOptions(settings) {
  return {
    showLogo: isTruthySetting(pickPrinterSettingValue(settings, "logo")),
    showLogoText: isTruthySetting(
      pickPrinterSettingValue(settings, "logo text")
    ),
    showGstNo: isTruthySetting(pickPrinterSettingValue(settings, "gstno")),
    showQr:
      isTruthySetting(pickPrinterSettingValue(settings, "qr")) ||
      isTruthySetting(pickPrinterSettingValue(settings, "qr code")) ||
      true,
    footerMsg:
      pickPrinterSettingValue(settings, "footer message")?.trim() || "",
  };
}

function pickLogoUrl(showLogo) {
  if (!showLogo) return null;
  return getAbsolutePublicAssetUrl(THERMAL_LOGO_PUBLIC_PATH);
}

/**
 * @param {object} params
 * @param {object} params.bill
 * @param {Array} params.trips
 * @param {string} params.vendorName
 * @param {Array} params.settings
 * @param {string} [params.billDateFormatted]
 */
export function buildThermalBillReceiptPayload({
  bill,
  trips = [],
  vendorName,
  settings,
  billDateFormatted,
}) {
  const printerInterface =
    process.env.REACT_APP_THERMAL_PRINTER_INTERFACE ||
    "winspool:RONGTA RP80 Printer";
  const printerType = process.env.REACT_APP_THERMAL_PRINTER_TYPE || "epson";
  const width = Number(process.env.REACT_APP_THERMAL_PRINT_WIDTH) || 48;
  const colWidths = tripTableColWidths(width);
  const tripTableCol = (index) => colWidths[index];

  const opts = getReceiptPrinterDisplayOptions(settings);
  const logoUrl = pickLogoUrl(opts.showLogo);

  const lines = [
    { kind: "lineSpacing", dots: 20 },
    { kind: "align", value: "center" },
  ];

  if (logoUrl) {
    const imageLine = {
      kind: "imageUrl",
      url: logoUrl,
      maxWidthPx: 160,
      maxHeightPx: 72,
      afterNewline: false,
    };
    const mime = guessImageMimeFromUrl(logoUrl);
    if (mime) imageLine.mimeType = mime;
    lines.push(imageLine);
    lines.push({ kind: "newline" });
  }

  if (opts.showLogoText) {
    lines.push({
      kind: "text",
      value: COMPANY_NAME,
      size: "doubleWidth",
      bold: true,
    });
    lines.push({ kind: "newline" });
  }

  lines.push({ kind: "text", value: COMPANY_ADDRESS });
  lines.push({ kind: "newline" });
  lines.push({ kind: "text", value: "BILL RECEIPT", bold: true });
  lines.push({ kind: "newline" });
  lines.push({ kind: "align", value: "left" });

  const billNo = bill?.id ?? "";
  const billDate = billDateFormatted || bill?.date || "";

  lines.push({
    kind: "leftRight",
    left: `Vendor Name : ${vendorName || "N/A"}`,
    right: `Bill No : ${billNo}`,
  });
  lines.push({ kind: "newline" });
  lines.push({
    kind: "leftRight",
    left: "",
    right: `Date : ${billDate}`,
  });
  lines.push({ kind: "newline" });
  lines.push({ kind: "line" });
  lines.push({ kind: "newline" });

  lines.push({
    kind: "table",
    columns: ["Sl.No", "Km", "Rate", "Ton", "Amount", "Wgt", "Adv"].map(
      (text, i) => ({
        text: fitCell(text, tripTableCol(i)),
        align: i === 0 ? "LEFT" : "RIGHT",
        cols: tripTableCol(i),
        bold: true,
      })
    ),
  });
  lines.push({ kind: "newline" });
  lines.push({ kind: "newline" });
  lines.push({ kind: "line" });
  lines.push({ kind: "newline" });

  trips.forEach((t, i) => {
    const vehicleNumber = t.vehicle_number || t.vehicleNumber || "N/A";
    const tripDate = t.date_formatted || t.date || "";
    lines.push({
      kind: "text",
      value: `${tripDate}  ${vehicleNumber}`,
    });
    lines.push({ kind: "newline" });

    const route = [t.source_name, t.destination_name].filter(Boolean).join(" - ");
    const balanceStr = fmt2(t.balance_amount ?? t.balance);
    // lines.push({
    //   kind: "text",
    //   value: padBetween(route, balanceStr, width),
    //   bold: true,
    // });
   lines.push({
     kind: "table",
     bold: true,
     columns: [
       {
         text: padBetween(route, balanceStr, width),
         cols: width,
         align: "LEFT",
         bold:true
       },
     ],
   });
    lines.push({ kind: "newline" });

    const tripValues = [
      String(i + 1),
      String(t.total_km ?? t.km ?? ""),
      fmt2(t.rate),
      String(t.total_tones ?? t.ton ?? ""),
      fmt2(t.amount),
      fmt2(t.transition_charge ?? t.wgt),
      fmt2(t.advance_amount ?? t.adv),
    ];
    lines.push({
      kind: "table",
      columns: tripValues.map((text, i) => ({
        text: fitCell(text, tripTableCol(i)),
        align: i === 0 ? "LEFT" : "RIGHT",
        cols: tripTableCol(i),
      })),
    });
    lines.push({ kind: "newline" });
    lines.push({ kind: "newline" });
  });

  lines.push({ kind: "line" });
  lines.push({ kind: "newline" });

  lines.push({
    kind: "text",
    value: padBetween(
      `Normal Ded. : ${fmt2(bill?.nor_advance_deduction)}`,
      `Total Amount : ${fmt2(bill?.total_amount)}`,
      width
    ),
  });
  lines.push({ kind: "newline" });
  lines.push({
    kind: "text",
    value: padBetween(
      `Receipt Ded. : ${fmt2(bill?.res_advance_deduction)}`,
      `Payable Amount : ${fmt2(bill?.payable_amount)}`,
      width
    ),
    bold: true,
  });
  lines.push({ kind: "newline" });
  lines.push({ kind: "newline" });

  // if (opts.showQr) {
  //   lines.push({ kind: "align", value: "center" });
  //   lines.push({
  //     kind: "qr",
  //     value: `ASA-TRANSPORT-BILL-${billNo}`,
  //   });
  //   lines.push({ kind: "newline" });
  // }

  if (opts.footerMsg) {
    lines.push({ kind: "align", value: "center" });
    lines.push({ kind: "text", value: opts.footerMsg });
    lines.push({ kind: "newline" });
  }

  return {
    printerInterface,
    printerType,
    width,
    initialFeedLines: 0,
    cutFeedLines: 0,
    lines,
    cut: true,
  };
}

export async function postThermalBillReceipt(payload) {
  const body = buildThermalBillReceiptPayload(payload);

  try {
    const res = await fetch(THERMAL_PRINT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      return {
        attempted: true,
        ok: false,
        error: errText || `HTTP ${res.status}`,
      };
    }
    return { attempted: true, ok: true };
  } catch (e) {
    return {
      attempted: true,
      ok: false,
      error: e?.message || "Network error",
    };
  }
}

export async function postThermalBillReceiptIfNeeded(params) {
  if (!isThermalPrinterFromSettings(params.settings)) {
    return { attempted: false, ok: true };
  }
  return postThermalBillReceipt(params);
}
